import { AxiosResponse } from "axios";
import { parse } from "query-string";
import DOMPurify from "dompurify";
import UnreadItemController from "controllers/layouts/message_threads/unread_item_controller";
import axios from "lib/axios";
import BaseController from "confinus_ui/base_controller";
import parseDateTime from "lib/util/parse_datetime_attr";
import { MessageNotificationEvent } from "lib/types";
import { Layouts__MessageThreads__UnreadItemControllerIdentifier } from "controllers/identifiers";

export default class UnreadMessagesController extends BaseController {
  public static targets = ["items"];

  public static values = {
    threadEncodedId: { type: String, default: null },
  };

  declare private readonly itemsTarget: HTMLDivElement;
  declare private readonly threadEncodedIdValue: any;

  public static outlets = [
    Layouts__MessageThreads__UnreadItemControllerIdentifier,
  ];
  declare private readonly layoutsMessageThreadsUnreadItemOutlets: UnreadItemController[];
  private get messageControllers() {
    return this.layoutsMessageThreadsUnreadItemOutlets;
  }

  public connect() {
    super.connect();
    if ("InstallTrigger" in window) {
      this.itemsTarget.style.minHeight = "0";
    }
  }

  public load(e: CustomEvent): void {
    const {
      detail: { message },
    } = e as MessageNotificationEvent;
    if (message.message_thread_encoded_id !== this.threadEncodedIdValue) {
      this.fetchUnread(message.message_thread_encoded_id);
    }
  }

  private fetchUnread(messageThreadUid: string): void {
    axios
      .get(this.unreadUrl(messageThreadUid))
      .then((response: AxiosResponse) => response.data)
      .then((html) => this.addNotification(html, messageThreadUid))
      .catch((e: Error) => {
        alert(e.message);
      });
  }

  private addNotification(html: string, messageThreadUid: string) {
    const threadNotification = this.messageControllers.find(
      (controller: UnreadItemController) =>
        controller.threadUid === messageThreadUid,
    );

    if (threadNotification) {
      // Notification item exsists, remove old content
      this.removeItem(threadNotification);
    }

    this.prependNotificationNode(html);
  }

  private prependNotificationNode(html: string) {
    const sentAt = parseDateTime(html);
    const afterNode = this.firstItemSentBefore(sentAt);

    // Sanitize HTML to prevent XSS attacks before inserting into DOM
    // This provides defense-in-depth even though Rails escapes on the server
    const sanitizedHtml = DOMPurify.sanitize(html, {
      // Allow data-* attributes for Stimulus controllers and other safe attributes
      ADD_ATTR: ["data-controller", "data-action", "data-target", "datetime"],
      // Keep existing content structure
      KEEP_CONTENT: true,
    });

    afterNode
      ? afterNode.scope.element.insertAdjacentHTML("beforebegin", sanitizedHtml)
      : this.itemsTarget.insertAdjacentHTML("afterbegin", sanitizedHtml);
  }

  private unreadUrl(messageThreadUid: string): string {
    let url = `/account/messages/threads/${messageThreadUid}/messages/unread`;
    const parts = parse(window.location.search);

    if (parts.shared_inbox_encoded_id) {
      url += `?shared_inbox_encoded_id=${parts.shared_inbox_encoded_id}`;
    }

    return url;
  }

  private firstItemSentBefore(
    sentAt: number,
  ): UnreadItemController | undefined {
    return this.messageControllers.find(
      (controller: UnreadItemController) => controller.sentAt < sentAt,
    );
  }

  private removeItem(controller: UnreadItemController) {
    const index = this.messageControllers.indexOf(controller);
    controller.scope.element.remove();
    this.messageControllers.splice(index, 1);
  }
}
